import { Navigate, useLocation } from 'react-router-dom';

import { useSession } from '@/lib/session';
import { may } from '@/lib/may';
import { Skeleton } from '@/components/ui/Skeleton';
import { WrongDoor } from './WrongDoor';

/**
 * The console gate, by permission.
 *
 * ── WHY IT TAKES A PERMISSION RATHER THAN ASKING `isAdmin` ──────────────────
 *
 * Not everybody with a job in the console has the same job. A clinical
 * reviewer reads reports that a medicine caused harm and decides them; they
 * have no business in settings, settlements or seller banking. Hiding those
 * menu items would be a suggestion about where to look. A gate per route is a
 * rule about what may be opened, and it is the one that holds when somebody
 * types a URL.
 *
 * The string is the SAME one the route's `can:` middleware checks on the
 * server, so widening somebody's role widens both at once and neither can
 * drift from the other.
 *
 * The API already refuses an unauthorised caller (`auth:sanctum` + the `admin`
 * middleware), so this is not the security boundary — the server is. This
 * exists so an unauthenticated admin sees a SIGN-IN SCREEN rather than three
 * screens of skeletons that never resolve, which is what a client-side 401 with
 * no handling actually looks like.
 */
export function RequireAdmin({
  children,
  // What this room takes. Omitted on the shell itself, where being staff at
  // all is the whole question and each child route asks its own.
  permission,
}: {
  children: React.ReactNode;
  // One permission, or ANY of several. The blog desk is reachable by a writer
  // who may not publish and by an editor who only reviews, and those are two
  // different grants — which is also how the route's `can:` middleware reads a
  // comma-separated list, so the two cannot drift apart.
  permission?: string | string[];
}) {
  const { isLoading, isUnauthenticated, user } = useSession();
  const location = useLocation();

  const isStaff =
    user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'pharmacist';

  const mayPass = Boolean(
    isStaff &&
      (permission === undefined ||
        (Array.isArray(permission)
          ? permission.some((one) => may(user, one))
          : may(user, permission))),
  );

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-[1100px] space-y-4 px-4 py-6">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-64 w-full" radius="card" />
      </div>
    );
  }

  if (isUnauthenticated) {
    // Remember where they were going, so signing in lands them there rather
    // than dumping them on the console home.
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  // Signed in, but not staff at all — a pharmacy owner who followed an old
  // bookmark. A plain explanation and two ways onward, rather than a dead end
  // that reads as "your account is broken", which is what this was.
  if (!isStaff) {
    return <WrongDoor title="This area is for ChiCare staff" user={user} />;
  }

  // Staff, but this is not their room. Worded differently on purpose: the
  // first message tells somebody they are at the wrong building, and this one
  // tells a colleague their job does not cover this screen — which is a
  // question for whoever set their role, not a sign-in problem.
  if (!mayPass) {
    return (
      <WrongDoor
        title="Your role doesn't cover this screen"
        body="Ask whoever manages your team if you need it."
        user={user}
      />
    );
  }

  return <>{children}</>;
}
